import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import attachmentIcon from '@adsk/bim360-matrix-react-assets/attachment-icon.svg';
import docNotAvailable from '@adsk/bim360-matrix-react-assets/doc-not-available.svg';
import modalCloseSvg from '@adsk/bim360-matrix-react-assets/modal-close.svg';

import './_FileItem.scss';

/**
 * FileItem component. Shows upload progress and allows upload cancel.
 */
export default class FileItem extends PureComponent {
  renderCancelButton() {
    if (this.props.onClose) {
      return (
        <IconButton
          className="FileItem__close-button"
          data-testid="FileItem__close-button"
          onClick={this.props.onClose}
        >
          <SvgIcon height="13" width="13">
            {modalCloseSvg}
          </SvgIcon>
        </IconButton>
      );
    }
    return null;
  }

  renderAttachmentIcon() {
    if (this.props.isBroken) {
      return (
        <SvgIcon
          key="brokenAttachmentIcon"
          className="FileItem__attachment-icon-svg"
          data-testid="FileItem__attachment-icon-unavailable"
          width="20px"
          height="25px"
        >
          {docNotAvailable}
        </SvgIcon>
      );
    } else if (this.props.thumbnailUrl) {
      return (
        <img
          key="thumbnailAttachmentIcon"
          data-testid="FileItem__attachment-icon-img"
          src={this.props.thumbnailUrl}
          width="20px"
          height="25px"
          onLoad={this.props.onLoadThumbnail}
        />
      );
    } else {
      return (
        <SvgIcon
          key="defaultAttachmentIcon"
          className="FileItem__attachment-icon-svg"
          data-testid="FileItem__attachment-icon-default"
          width="20px"
          height="25px"
        >
          {attachmentIcon}
        </SvgIcon>
      );
    }
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      className,
      thumbnailUrl,
      onLoadThumbnail,
      renderFooter,
      onClose,
      children,
      isBroken,
      topBarRenderer,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */
    const topBarChildren = [
      this.renderAttachmentIcon(),
      <div key="content" className="FileItem__content">
        {children}
      </div>
    ];

    return (
      <div {...rest} className={classNames('FileItem', className)}>
        <div
          className={classNames('FileItem__top-bar-container', {
            'FileItem__top-bar-container--with-close-button': onClose
          })}
        >
          {topBarRenderer({ children: topBarChildren })}
          {this.renderCancelButton()}
        </div>
        {renderFooter && renderFooter()}
      </div>
    );
  }
}

/* eslint-disable react/prop-types, react/display-name */
FileItem.defaultTopBar = ({ children }) => <div className="FileItem__top-bar">{children}</div>;
FileItem.clickableTopBar = onClick => ({ children }) => (
  <div className="FileItem__top-bar FileItem__top-bar--clickable" role="button" onClick={onClick}>
    {children}
  </div>
);
/* eslint-enable react/prop-types, react/display-name */

FileItem.defaultProps = {
  isBroken: false,
  topBarRenderer: FileItem.defaultTopBar
};

FileItem.propTypes = {
  /**
   * An additional classname to assign to FileItem
   */
  className: PropTypes.string,
  /**
   * A function that receives `children` and returns a node.
   * Used to customize the topBarRenderer according to your needs.
   * The most common use case is to make the top bar clickable. Take a look at FileItem Story for an example.
   */
  topBarRenderer: PropTypes.func,
  /**
   * An optional thumbnail to show next to the file content.
   */
  thumbnailUrl: PropTypes.string,
  /**
   * Callback for thumbnail onLoad
   */
  onLoadThumbnail: PropTypes.func,
  /**
   * Render function for footer.
   */
  renderFooter: PropTypes.func,
  /**
   * Handles click on FileItem
   */
  onClick: PropTypes.func,
  /**
   * Handle close button click.
   */
  onClose: PropTypes.func,
  /**
   * The content to show in the File Item.
   */
  children: PropTypes.node.isRequired,
  /**
   * Indicates if the file is broken for some reason (displays an unavailable thumbnail).
   */
  isBroken: PropTypes.bool
};
